'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';

// ─── COMMUNITY MAP — Caretaker Gardens, Resource Flows, Quest Chains, Impact Hubs, Mata Lineages ──

interface Garden {
  id: string;
  name: string;
  city: string;
  ward: string;
  caretaker: string;
  type: 'community' | 'masjid' | 'urban-farm' | 'mutual-aid' | 'reentry';
  status: 'active' | 'growing' | 'seed';
  mataCount: number;
  questCount: number;
  impactScore: number;
  lat: number;
  lng: number;
  x: number; // normalized 0-100 for SVG
  y: number;
  color: string;
  lineages: string[];
  resources: { type: string; flow: 'in' | 'out' | 'both'; amount: string }[];
  description: string;
}

interface ResourceFlow {
  from: string;
  to: string;
  type: string;
  amount: string;
  color: string;
}

interface QuestChain {
  id: string;
  title: string;
  gardens: string[];
  status: 'active' | 'complete' | 'forming';
  observations: number;
  actions: number;
  color: string;
}

interface ImpactHub {
  id: string;
  name: string;
  city: string;
  type: 'coordination' | 'distribution' | 'training' | 'sanctuary';
  gardenCount: number;
  x: number;
  y: number;
  color: string;
}

interface MataLineage {
  id: string;
  name: string;
  arabic: string;
  rootGarden: string;
  branches: string[];
  generation: number;
  color: string;
  description: string;
}

// ── Sample data ───────────────────────────────────────────────────────────────
const GARDENS: Garden[] = [
  { id: 'g1', name: 'IMAN Englewood Garden', city: 'Chicago', ward: 'Englewood', caretaker: 'IMAN Chicago', type: 'community', status: 'active', mataCount: 8, questCount: 5, impactScore: 92, lat: 41.78, lng: -87.64, x: 35, y: 45, color: '#2d7a47', lineages: ['l1', 'l2'], resources: [{ type: 'Food', flow: 'out', amount: '200 meals/wk' }, { type: 'Training', flow: 'in', amount: 'Weekly' }], description: 'IMAN\'s flagship community garden in Englewood — food production, reentry support, and IRL Lens observations.' },
  { id: 'g2', name: 'Brightmoor Farmway', city: 'Detroit', ward: 'Brightmoor', caretaker: 'Detroit Caretakers', type: 'urban-farm', status: 'active', mataCount: 6, questCount: 3, impactScore: 85, lat: 42.38, lng: -83.17, x: 62, y: 30, color: '#c9a84c', lineages: ['l1', 'l3'], resources: [{ type: 'Food', flow: 'out', amount: '150 lbs/wk' }, { type: 'Seeds', flow: 'both', amount: 'Seasonal' }], description: 'Urban farm on vacant lots — the Hadith of the palm shoot made operational.' },
  { id: 'g3', name: 'Masjid As-Saber Garden', city: 'Portland', ward: 'East Portland', caretaker: 'Masjid As-Saber', type: 'masjid', status: 'growing', mataCount: 4, questCount: 2, impactScore: 71, lat: 45.52, lng: -122.59, x: 12, y: 25, color: '#2d8a87', lineages: ['l2'], resources: [{ type: 'Community', flow: 'out', amount: 'Weekly gathering' }], description: 'The Patient Masjid — named for the Ayah. Garden as sanctuary in the displacement zone.' },
  { id: 'g4', name: 'Atlanta West End Hub', city: 'Atlanta', ward: 'West End', caretaker: 'Community Masjid ATL', type: 'mutual-aid', status: 'active', mataCount: 7, questCount: 4, impactScore: 88, lat: 33.73, lng: -84.42, x: 55, y: 68, color: '#8a8ab8', lineages: ['l1', 'l4'], resources: [{ type: 'Food', flow: 'both', amount: '300 meals/wk' }, { type: 'Housing', flow: 'in', amount: 'Referrals' }], description: 'Mutual aid hub in the West End — bridging displacement with community provision.' },
  { id: 'g5', name: 'South Shore Reentry Garden', city: 'Chicago', ward: 'South Shore', caretaker: 'IMAN Green ReEntry', type: 'reentry', status: 'active', mataCount: 5, questCount: 6, impactScore: 79, lat: 41.76, lng: -87.57, x: 40, y: 52, color: '#2d7a47', lineages: ['l1', 'l5'], resources: [{ type: 'Training', flow: 'out', amount: 'Daily' }, { type: 'Employment', flow: 'out', amount: '12 placed/mo' }], description: 'IMAN Green ReEntry — returning citizens as caretakers of the earth.' },
  { id: 'g6', name: 'Hamtramck Community Plot', city: 'Detroit', ward: 'Hamtramck', caretaker: 'Hamtramck Islamic Center', type: 'community', status: 'growing', mataCount: 3, questCount: 2, impactScore: 65, lat: 42.39, lng: -83.05, x: 67, y: 35, color: '#c9a84c', lineages: ['l3'], resources: [{ type: 'Food', flow: 'out', amount: '80 meals/wk' }], description: 'First all-Muslim city council — community garden as civic expression.' },
];

const RESOURCE_FLOWS: ResourceFlow[] = [
  { from: 'g1', to: 'g5', type: 'Training', amount: 'Weekly', color: '#2d7a47' },
  { from: 'g2', to: 'g6', type: 'Seeds', amount: 'Seasonal', color: '#c9a84c' },
  { from: 'g4', to: 'g3', type: 'Network', amount: 'Monthly', color: '#8a8ab8' },
  { from: 'g1', to: 'g4', type: 'Coordination', amount: 'Bi-weekly', color: '#2d8a87' },
];

const QUEST_CHAINS: QuestChain[] = [
  { id: 'qc1', title: 'Iftar on Wheels — Chicago', gardens: ['g1', 'g5'], status: 'active', observations: 12, actions: 8, color: '#c9a84c' },
  { id: 'qc2', title: 'Detroit Urban Farm Network', gardens: ['g2', 'g6'], status: 'active', observations: 7, actions: 5, color: '#2d7a47' },
  { id: 'qc3', title: 'Displacement Bridge — Atlanta/Portland', gardens: ['g3', 'g4'], status: 'forming', observations: 3, actions: 1, color: '#8a8ab8' },
  { id: 'qc4', title: 'Reentry Caretaker Chain', gardens: ['g1', 'g5', 'g4'], status: 'active', observations: 18, actions: 14, color: '#2d8a87' },
];

const IMPACT_HUBS: ImpactHub[] = [
  { id: 'h1', name: 'IMAN Chicago HQ', city: 'Chicago', type: 'coordination', gardenCount: 3, x: 37, y: 48, color: '#2d7a47' },
  { id: 'h2', name: 'Detroit Caretaker Network', city: 'Detroit', type: 'distribution', gardenCount: 2, x: 64, y: 32, color: '#c9a84c' },
  { id: 'h3', name: 'Atlanta Masjid Hub', city: 'Atlanta', type: 'sanctuary', gardenCount: 2, x: 57, y: 65, color: '#8a8ab8' },
];

const MATA_LINEAGES: MataLineage[] = [
  { id: 'l1', name: 'IRL Lens Framework', arabic: 'عَدَسَة الْوَاقِع', rootGarden: 'g1', branches: ['g4', 'g5'], generation: 3, color: '#2d8a87', description: 'The IRL Lens methodology transplanted from IMAN Chicago to Atlanta and South Shore.' },
  { id: 'l2', name: 'Masjid Garden Model', arabic: 'نَمُوذَج حَدِيقَة الْمَسْجِد', rootGarden: 'g3', branches: ['g1'], generation: 2, color: '#2d7a47', description: 'The Masjid As-Saber garden model — sanctuary as ecological practice.' },
  { id: 'l3', name: 'Urban Farm Lineage', arabic: 'سِلَالَة الزِّرَاعَة الْحَضَرِيَّة', rootGarden: 'g2', branches: ['g6'], generation: 2, color: '#c9a84c', description: 'Brightmoor Farmway methodology — vacant lot to food production.' },
  { id: 'l4', name: 'Mutual Aid Network', arabic: 'شَبَكَة التَّعَاوُن', rootGarden: 'g4', branches: ['g3'], generation: 2, color: '#8a8ab8', description: 'Atlanta West End mutual aid model — bridging displacement with provision.' },
  { id: 'l5', name: 'Green ReEntry Model', arabic: 'نَمُوذَج الإِعَادَة الْخَضْرَاء', rootGarden: 'g5', branches: [], generation: 1, color: '#E05252', description: 'IMAN Green ReEntry — returning citizens as caretakers of the earth.' },
];

const GARDEN_TYPE_ICONS: Record<string, string> = {
  community: '⬡',
  'masjid': '🕌',
  'urban-farm': '🌱',
  'mutual-aid': '🤝',
  reentry: '◎',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#2d7a47',
  growing: '#c9a84c',
  seed: 'rgba(212,197,169,0.3)',
};

type MapView = 'gardens' | 'flows' | 'quests' | 'hubs' | 'lineages';

export default function CommunityMap() {
  const [activeView, setActiveView] = useState<MapView>('gardens');
  const [selectedGarden, setSelectedGarden] = useState<Garden | null>(null);
  const [selectedLineage, setSelectedLineage] = useState<MataLineage | null>(null);
  const [selectedQuest, setSelectedQuest] = useState<QuestChain | null>(null);
  const [cityFilter, setCityFilter] = useState<string>('all');

  const cities = useMemo(() => ['all', ...Array.from(new Set(GARDENS.map(g => g.city)))], []);
  const filteredGardens = useMemo(() => cityFilter === 'all' ? GARDENS : GARDENS.filter(g => g.city === cityFilter), [cityFilter]);

  const VIEW_TABS: { key: MapView; label: string; icon: string }[] = [
    { key: 'gardens', label: 'Gardens', icon: '🌿' },
    { key: 'flows', label: 'Resource Flows', icon: '⬡' },
    { key: 'quests', label: 'Quest Chains', icon: '⌘' },
    { key: 'hubs', label: 'Impact Hubs', icon: '◎' },
    { key: 'lineages', label: 'Mata Lineages', icon: '✦' },
  ];

  return (
    <section style={{ background: '#060d12', borderTop: '1px solid rgba(45,122,71,0.1)', padding: '60px 0' }} id="community-map">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', marginBottom: 8 }}>
            ⬡ COMMUNITY MAP · خَرِيطَة الْجَمَاعَة
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(16px, 3vw, 24px)', color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Caretaker Gardens Across Cities
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 12, lineHeight: 1.7, maxWidth: 600 }}>
            An interactive map of caretaker gardens, resource flows between wards, quest chains linking observations to actions, community impact hubs, and interconnected mata plant lineages.
          </p>
        </div>

        {/* View tabs */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20 }}>
          {VIEW_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveView(tab.key)} style={{ padding: '7px 14px', background: activeView === tab.key ? 'rgba(45,122,71,0.15)' : 'transparent', border: `1px solid ${activeView === tab.key ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.12)'}`, borderRadius: 4, color: activeView === tab.key ? '#2d7a47' : 'rgba(45,122,71,0.4)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* City filter */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20 }}>
          {cities.map(city => (
            <button key={city} onClick={() => setCityFilter(city)} style={{ padding: '4px 10px', background: cityFilter === city ? 'rgba(45,122,71,0.12)' : 'transparent', border: `1px solid ${cityFilter === city ? 'rgba(45,122,71,0.35)' : 'rgba(45,122,71,0.1)'}`, borderRadius: 20, color: cityFilter === city ? '#2d7a47' : 'rgba(45,122,71,0.35)', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer' }}>
              {city === 'all' ? 'All Cities' : city}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
          {/* Map canvas */}
          <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 8, overflow: 'hidden', position: 'relative', minHeight: 400 }}>
            <svg width="100%" height="400" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
              {/* Background grid */}
              {Array.from({ length: 10 }).map((_, i) => (
                <React.Fragment key={i}>
                  <line x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="rgba(45,122,71,0.05)" strokeWidth="0.3" />
                  <line x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="rgba(45,122,71,0.05)" strokeWidth="0.3" />
                </React.Fragment>
              ))}

              {/* Resource flows */}
              {(activeView === 'flows' || activeView === 'gardens') && RESOURCE_FLOWS.map((flow, i) => {
                const from = GARDENS.find(g => g.id === flow.from);
                const to = GARDENS.find(g => g.id === flow.to);
                if (!from || !to) return null;
                if (cityFilter !== 'all' && from.city !== cityFilter && to.city !== cityFilter) return null;
                return (
                  <g key={i}>
                    <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={flow.color} strokeWidth="0.5" strokeDasharray="2,1.5" opacity="0.4" />
                    <circle cx={(from.x + to.x) / 2} cy={(from.y + to.y) / 2} r="0.8" fill={flow.color} opacity="0.6" />
                  </g>
                );
              })}

              {/* Quest chain connections */}
              {activeView === 'quests' && QUEST_CHAINS.map((quest) => {
                const gardens = quest.gardens.map(id => GARDENS.find(g => g.id === id)).filter(Boolean) as Garden[];
                return gardens.slice(0, -1).map((g, i) => {
                  const next = gardens[i + 1];
                  if (!next) return null;
                  return (
                    <line key={`${quest.id}-${i}`} x1={g.x} y1={g.y} x2={next.x} y2={next.y} stroke={quest.color} strokeWidth="0.8" opacity={selectedQuest?.id === quest.id ? 0.9 : 0.3} strokeDasharray={quest.status === 'forming' ? '1.5,1' : 'none'} />
                  );
                });
              })}

              {/* Lineage connections */}
              {activeView === 'lineages' && MATA_LINEAGES.map((lineage) => {
                const root = GARDENS.find(g => g.id === lineage.rootGarden);
                if (!root) return null;
                return lineage.branches.map((branchId, i) => {
                  const branch = GARDENS.find(g => g.id === branchId);
                  if (!branch) return null;
                  return (
                    <line key={`${lineage.id}-${i}`} x1={root.x} y1={root.y} x2={branch.x} y2={branch.y} stroke={lineage.color} strokeWidth="0.6" opacity={selectedLineage?.id === lineage.id ? 0.9 : 0.25} />
                  );
                });
              })}

              {/* Impact hubs */}
              {activeView === 'hubs' && IMPACT_HUBS.map(hub => (
                <g key={hub.id}>
                  <circle cx={hub.x} cy={hub.y} r="4" fill={hub.color} opacity="0.1" />
                  <circle cx={hub.x} cy={hub.y} r="2.5" fill={hub.color} opacity="0.2" stroke={hub.color} strokeWidth="0.5" />
                  <text x={hub.x} y={hub.y + 0.5} textAnchor="middle" dominantBaseline="middle" fill={hub.color} fontSize="2" fontFamily="monospace">◎</text>
                </g>
              ))}

              {/* Gardens */}
              {filteredGardens.map(garden => {
                const isSelected = selectedGarden?.id === garden.id;
                const isInQuest = selectedQuest ? selectedQuest.gardens.includes(garden.id) : true;
                const isInLineage = selectedLineage ? (selectedLineage.rootGarden === garden.id || selectedLineage.branches.includes(garden.id)) : true;
                const opacity = (activeView === 'quests' && selectedQuest && !isInQuest) ? 0.2 :
                  (activeView === 'lineages' && selectedLineage && !isInLineage) ? 0.2 : 1;
                return (
                  <g key={garden.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedGarden(isSelected ? null : garden)} opacity={opacity}>
                    <circle cx={garden.x} cy={garden.y} r={isSelected ? 4 : 3} fill={garden.color} opacity="0.15" />
                    <circle cx={garden.x} cy={garden.y} r={isSelected ? 2.5 : 2} fill={garden.color} opacity={isSelected ? 0.9 : 0.6} stroke={isSelected ? '#F5F0E8' : garden.color} strokeWidth={isSelected ? 0.5 : 0.3} />
                    <text x={garden.x} y={garden.y + 4.5} textAnchor="middle" fill="rgba(212,197,169,0.5)" fontSize="1.8" fontFamily="monospace">{garden.city.slice(0, 3)}</text>
                  </g>
                );
              })}
            </svg>

            {/* Legend */}
            <div style={{ position: 'absolute', bottom: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {Object.entries(GARDEN_TYPE_ICONS).map(([type, icon]) => (
                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 8 }}>{icon}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{type}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Side panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Garden detail */}
            {selectedGarden && (
              <div style={{ background: 'rgba(6,13,18,0.9)', border: `1px solid ${selectedGarden.color}30`, borderRadius: 8, padding: '16px', borderTop: `3px solid ${selectedGarden.color}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: selectedGarden.color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>{GARDEN_TYPE_ICONS[selectedGarden.type]} {selectedGarden.type}</p>
                    <h3 style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700, marginBottom: 2 }}>{selectedGarden.name}</h3>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>{selectedGarden.city} · {selectedGarden.ward}</p>
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: STATUS_COLORS[selectedGarden.status], background: `${STATUS_COLORS[selectedGarden.status]}15`, border: `1px solid ${STATUS_COLORS[selectedGarden.status]}30`, padding: '2px 6px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase', flexShrink: 0 }}>{selectedGarden.status}</span>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, lineHeight: 1.5, marginBottom: 10 }}>{selectedGarden.description}</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 10 }}>
                  {[{ label: 'Mata', value: selectedGarden.mataCount }, { label: 'Quests', value: selectedGarden.questCount }, { label: 'Impact', value: selectedGarden.impactScore + '%' }].map((stat, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: '6px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: selectedGarden.color, marginBottom: 1 }}>{stat.value}</p>
                      <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{stat.label}</p>
                    </div>
                  ))}
                </div>
                {/* Resource flows */}
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Resource Flows</p>
                  {selectedGarden.resources.map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, color: r.flow === 'out' ? '#2d7a47' : r.flow === 'in' ? '#c9a84c' : '#2d8a87' }}>{r.flow === 'out' ? '→' : r.flow === 'in' ? '←' : '⇄'}</span>
                      <span style={{ color: 'rgba(212,197,169,0.6)', fontSize: 9 }}>{r.type}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', marginLeft: 'auto' }}>{r.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quest chains list */}
            {activeView === 'quests' && (
              <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Quest Chains · {QUEST_CHAINS.length}</p>
                </div>
                {QUEST_CHAINS.map(quest => (
                  <div key={quest.id} onClick={() => setSelectedQuest(selectedQuest?.id === quest.id ? null : quest)} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(201,168,76,0.06)', cursor: 'pointer', background: selectedQuest?.id === quest.id ? `${quest.color}08` : 'transparent', transition: 'background 0.15s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: quest.color, background: `${quest.color}15`, border: `1px solid ${quest.color}25`, padding: '1px 5px', borderRadius: 10, letterSpacing: '.06em', textTransform: 'uppercase' }}>{quest.status}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>{quest.gardens.length} gardens</span>
                    </div>
                    <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', marginBottom: 4 }}>{quest.title}</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,138,135,0.6)' }}>◎ {quest.observations} obs</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(45,122,71,0.6)' }}>⬡ {quest.actions} actions</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Lineages list */}
            {activeView === 'lineages' && (
              <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Mata Plant Lineages · {MATA_LINEAGES.length}</p>
                </div>
                {MATA_LINEAGES.map(lineage => (
                  <div key={lineage.id} onClick={() => setSelectedLineage(selectedLineage?.id === lineage.id ? null : lineage)} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(201,168,76,0.06)', cursor: 'pointer', background: selectedLineage?.id === lineage.id ? `${lineage.color}08` : 'transparent', transition: 'background 0.15s' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: lineage.color, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', marginBottom: 1 }}>{lineage.name}</p>
                        <p style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${lineage.color}70`, direction: 'rtl', marginBottom: 3 }}>{lineage.arabic}</p>
                        <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 8, lineHeight: 1.4 }}>{lineage.description}</p>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>Gen {lineage.generation}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>{lineage.branches.length} branches</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Gardens list */}
            {(activeView === 'gardens' || activeView === 'flows' || activeView === 'hubs') && !selectedGarden && (
              <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.12)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(45,122,71,0.1)' }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,122,71,0.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Gardens · {filteredGardens.length}</p>
                </div>
                {filteredGardens.map(garden => (
                  <div key={garden.id} onClick={() => setSelectedGarden(garden)} style={{ padding: '8px 14px', borderBottom: '1px solid rgba(45,122,71,0.06)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: garden.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{garden.name}</p>
                      <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>{garden.city} · {garden.ward}</p>
                    </div>
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: STATUS_COLORS[garden.status], flexShrink: 0 }}>{garden.impactScore}%</span>
                  </div>
                ))}
              </div>
            )}

            {/* Link to full asbab quest */}
            <Link href="/asbab-quest" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6, textDecoration: 'none' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.08em', textTransform: 'uppercase' }}>⌘ Asbab Quest Interface</span>
              <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,122,71,0.5)' }}>→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
